import { DeductionDetailJson, DeductionNote, save_note } from "@/api/deduction.tsx"
import { api_fetch } from "@/api/client.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Textarea } from "@/components/ui/textarea.tsx"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import { toast } from "@/components/ui/use-toast"
import { classes } from "@/utils"
import { capitalCase } from "change-case"
import { format } from "date-fns"
import { ArrowUp, BookmarkPlus, Check } from "lucide-react"
import { useRef, useState } from "preact/hooks"
import { NextActionCard } from "@/deduction_next_action"
import { PendingTasksCard } from "@/deduction_detail/PendingTasksCard.tsx"
import { ConversationCard } from "./Conversation.tsx"
import { BolEmailSyncCard } from "./BolEmailSync.tsx"
import { triggerRefreshFlag, RefreshFlag } from "@/refresh_flag.tsx"
import { WizardRunsSection } from "@/deduction_detail/WizardRunsSection.tsx"
import { ShipmentPanel } from "@/deduction_detail/ShipmentPanel.tsx"

export interface RightPaneProps {
  deduction: DeductionDetailJson
  className?: string
  onTriggerWizard?: () => void
  onFileDispute?: () => void
  onMatchOriginal?: () => void
}

export function RightPane({ deduction, className, onTriggerWizard, onFileDispute, onMatchOriginal }: RightPaneProps) {
  return (
    <div class={classes("space-y-4", className)}>
      <PendingTasksCard deduction={deduction} />
      <NextActionCard deduction={deduction} onTriggerWizard={onTriggerWizard} onFileDispute={onFileDispute} onMatchOriginal={onMatchOriginal} />
      {deduction.kind !== "repayment" && (
        <ShipmentPanel deduction_id={deduction.id} po_number={deduction.po_number} default_open={true} />
      )}
      <div id="notes" class="border-t-6 border-purple-500 bg-purple-50 rounded-md p-4">
        <span class="flex">
          <div class="w-1 bg-purple-500 mr-2" />
          <h3 class="text-lg font-semibold leading-5">Notes</h3>
        </span>
        <Notes notes={deduction.notes} deduction_id={deduction.id} />
      </div>
      <WizardRunsSection deduction_id={deduction.id} />
      <ConversationCard deduction={deduction} />
      <BolEmailSyncCard deduction={deduction} />
    </div>
  )
}

interface NoteTextAreaProps {
  deduction_id: string
}

function NoteTextArea({ deduction_id }: NoteTextAreaProps) {
  const [note, setNote] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const [loading, setLoading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  async function handle_save() {
    if (!note.trim()) {
      return
    }
    setLoading(true)
    await save_note(note, deduction_id)
    // Focus back on textarea after sending
    textareaRef.current?.focus()
    setNote("")
    setLoading(false)
    triggerRefreshFlag()
  }

  const showExpanded = isFocused || note.trim().length > 0

  return (
    <div className="">
      <div className="mt-4">
        <Textarea
          ref={textareaRef}
          value={note}
          onChange={(e: any) => setNote(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={async (e: any) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
              e.preventDefault()
              await handle_save()
            }
          }}
          className={classes(
            "rounded-md border border-gray-100 resize-none w-full text-md focus-visible:outline-hidden focus:ring-0 transition-all duration-200",
            showExpanded ? "p-2 min-h-20" : "py-2 px-2 h-10 min-h-0 overflow-hidden"
          )}
          placeholder="Add note..."
        />
      </div>
      <div
        className={classes(
          "flex justify-end mt-1 transition-all duration-200",
          showExpanded ? "opacity-100 max-h-10" : "opacity-0 max-h-0 overflow-hidden"
        )}>
        <Button variant={"secondary"} size={"sm" as any} disabled={!note.trim() || loading} onClick={handle_save}>
          Send
          <ArrowUp className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  )
}

interface SaveToSkillsButtonProps {
  note_message: string
  deduction_id: string
}

function SaveToSkillsButton({ note_message, deduction_id }: SaveToSkillsButtonProps) {
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  async function handleSave() {
    setSaving(true)
    setTooltipOpen(false)
    const res = await api_fetch("/organization/skills/note", {
      body: { note_message, deduction_id },
    })
    if (res.ok) {
      setSaved(true)
      toast({ title: "Saved to AI Knowledge Base", description: "This note will be referenced in future AI analyses" })
    } else {
      toast({ title: "Error", description: "Failed to save to AI knowledge base", variant: "destructive" })
    }
    setSaving(false)
  }

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <button
            onClick={handleSave}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
            onFocus={() => setTooltipOpen(true)}
            onBlur={() => setTooltipOpen(false)}
            disabled={saving || saved}
            class="text-gray-400 hover:text-purple-600 transition-colors disabled:opacity-50"
          >
            {saved
              ? <Check className="w-3.5 h-3.5 text-green-500" />
              : <BookmarkPlus className="w-3.5 h-3.5" />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={4}>
          <p>{saved ? "Saved to AI Knowledge Base" : "Save to AI Knowledge Base"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

interface InternalMessageProps {
  notes?: DeductionNote[]
  deduction_id: string
  className?: string
}

function Notes({ notes, deduction_id, className }: InternalMessageProps) {
  // Sort notes by created_at in ascending order to show oldest first
  notes = (notes ?? []).filter(note => note.message.trim() !== "")
  let sortedNotes = [...notes].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

  return (
    <div class={className}>
      {sortedNotes.length > 0 && (
        <div className="w-full gap-4 mt-4 grow overflow-auto">
          {sortedNotes.map(({ message, user_id, actor_name, created_at }: DeductionNote) => {
            const formattedDate = format(new Date(created_at), "PP")
            return (
              <div class="w-full">
                <p class="text-gray-500 break-words whitespace-pre-line p-2 bg-white rounded-md border border-gray-100">{message}</p>
                <div class="text-gray-500 text-sm flex items-center justify-between">
                  <span class="flex items-center gap-1.5">
                    {capitalCase(actor_name)}
                    <SaveToSkillsButton note_message={message} deduction_id={deduction_id} />
                  </span>
                  <span>{formattedDate}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <NoteTextArea deduction_id={deduction_id} />
    </div>
  )
}
